// Use native gapi from window object (loaded via script tag in index.html)
const gapi = window.gapi;

const CLIENT_ID = import.meta.env?.VITE_GOOGLE_CLIENT_ID;
const API_KEY = import.meta.env?.VITE_GOOGLE_API_KEY;
const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'];
const SCOPES = 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events';

let gapiInitialized = false;

// Initialize Google API client
export const initGoogleAPI = () => {
  return new Promise((resolve, reject) => {
    if (gapiInitialized) {
      resolve(true);
      return;
    }

    if (!gapi) {
      reject(new Error('Google API not loaded'));
      return;
    }

    gapi.load('client:auth2', async () => {
      try {
        await gapi.client.init({
          apiKey: API_KEY,
          clientId: CLIENT_ID,
          discoveryDocs: DISCOVERY_DOCS,
          scope: SCOPES,
        });
        gapiInitialized = true;
        resolve(true);
      } catch (error) {
        console.error('Error initializing Google API:', error);
        reject(error);
      }
    });
  });
};

// Check if user is signed in
export const isSignedIn = () => {
  if (!gapiInitialized) return false;
  return gapi?.auth2?.getAuthInstance()?.isSignedIn?.get() || false;
};

// Sign in to Google
export const signIn = async () => {
  try {
    await initGoogleAPI();
    const authInstance = gapi?.auth2?.getAuthInstance();
    await authInstance?.signIn();
    return true;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

// Sign out from Google
export const signOut = async () => {
  try {
    const authInstance = gapi?.auth2?.getAuthInstance();
    await authInstance?.signOut();
    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Get user's calendar list
export const getCalendarList = async () => {
  try {
    const response = await gapi?.client?.calendar?.calendarList?.list();
    return response?.result?.items || [];
  } catch (error) {
    console.error('Error fetching calendar list:', error);
    throw error;
  }
};

// Fetch events from Google Calendar
export const fetchGoogleCalendarEvents = async (calendarId = 'primary', timeMin, timeMax) => {
  try {
    const response = await gapi?.client?.calendar?.events?.list({
      calendarId: calendarId,
      timeMin: timeMin || new Date()?.toISOString(),
      timeMax: timeMax,
      showDeleted: false,
      singleEvents: true,
      orderBy: 'startTime',
    });

    return response?.result?.items || [];
  } catch (error) {
    console.error('Error fetching Google Calendar events:', error);
    throw error;
  }
};

// Convert Google Calendar event to app format
export const convertGoogleEventToActivity = (googleEvent, personId, personName, personColor, personInitials) => {
  const startDateTime = googleEvent?.start?.dateTime || googleEvent?.start?.date;
  const date = new Date(startDateTime);
  
  return {
    id: `google-${googleEvent?.id}`,
    title: googleEvent?.summary || 'Untitled Event',
    date: date?.toISOString()?.split('T')?.[0],
    time: googleEvent?.start?.dateTime ? date?.toTimeString()?.slice(0, 5) : '00:00',
    personId: personId,
    personName: personName,
    personColor: personColor,
    personInitials: personInitials,
    location: googleEvent?.location || '',
    category: 'imported',
    description: googleEvent?.description || '',
    googleEventId: googleEvent?.id,
    googleCalendarId: googleEvent?.organizer?.email || 'primary',
  };
};

// Create event in Google Calendar
export const createGoogleCalendarEvent = async (activity, calendarId = 'primary') => {
  try {
    const event = {
      summary: activity?.title,
      location: activity?.location || '',
      description: activity?.description || '',
      start: {
        dateTime: `${activity?.date}T${activity?.time}:00`,
        timeZone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
      },
      end: {
        dateTime: `${activity?.date}T${activity?.time}:00`,
        timeZone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
      },
    };

    const response = await gapi?.client?.calendar?.events?.insert({
      calendarId: calendarId,
      resource: event,
    });

    return response?.result;
  } catch (error) {
    console.error('Error creating Google Calendar event:', error);
    throw error;
  }
};

// Update event in Google Calendar
export const updateGoogleCalendarEvent = async (activity, calendarId = 'primary') => {
  try {
    if (!activity?.googleEventId) {
      throw new Error('No Google Event ID found');
    }

    const event = {
      summary: activity?.title,
      location: activity?.location || '',
      description: activity?.description || '',
      start: {
        dateTime: `${activity?.date}T${activity?.time}:00`,
        timeZone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
      },
      end: {
        dateTime: `${activity?.date}T${activity?.time}:00`,
        timeZone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
      },
    };

    const response = await gapi?.client?.calendar?.events?.update({
      calendarId: calendarId,
      eventId: activity?.googleEventId,
      resource: event,
    });

    return response?.result;
  } catch (error) {
    console.error('Error updating Google Calendar event:', error);
    throw error;
  }
};

// Delete event from Google Calendar
export const deleteGoogleCalendarEvent = async (eventId, calendarId = 'primary') => {
  try {
    await gapi?.client?.calendar?.events?.delete({
      calendarId: calendarId,
      eventId: eventId,
    });
    return true;
  } catch (error) {
    console.error('Error deleting Google Calendar event:', error);
    throw error;
  }
};